import axios from 'axios';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Login.css';
import { useAuth } from '../context/AuthContext';
import 'bootstrap-icons/font/bootstrap-icons.css';

const getRetryAfterMessage = (headers = {}) => {
  const retryAfter = headers['retry-after'];

  if (!retryAfter) {
    return 'Te veel inlogpogingen. Probeer het over 15 minuten opnieuw.';
  }

  const retryAfterSeconds = Number.parseInt(retryAfter, 10);

  if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
    const minutes = Math.ceil(retryAfterSeconds / 60);

    if (minutes <= 1) {
      return 'Te veel inlogpogingen. Probeer het over ongeveer 1 minuut opnieuw.';
    }

    return `Te veel inlogpogingen. Probeer het over ongeveer ${minutes} minuten opnieuw.`;
  }

  return 'Te veel inlogpogingen. Probeer het over 15 minuten opnieuw.';
};

const getApiErrorMessage = (err, fallbackMessage) => {
  const response = err?.response;

  if (!response) {
    return 'Geen verbinding met de server. Controleer je internetverbinding en probeer opnieuw.';
  }

  if (response.status === 429) {
    return getRetryAfterMessage(response.headers);
  }

  const data = response.data;

  if (typeof data === 'string' && data.trim()) {
    return data.trim();
  }

  return (
    data?.Error ||
    data?.error ||
    data?.message ||
    data?.Message ||
    fallbackMessage
  );
};

function Login() {
  const [values, setValues] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 2FA state
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [twoFactorChallenge, setTwoFactorChallenge] = useState(null);
  const [totpCode, setTotpCode] = useState('');

  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (event) => {
    event.preventDefault();

    if (isSubmitting) return;

    setError('');
    setIsSubmitting(true);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/auth/login`,
        values,
        { withCredentials: true }
      );

      if (res.data.Status === 'Success') {
        login(res.data.token);
        navigate('/home', { replace: true });
      } else if (res.data.Status === '2FA_REQUIRED') {
        // Wachtwoord klopt, maar 2FA is vereist
        setTwoFactorRequired(true);
        setTwoFactorChallenge(res.data.challenge);
      } else {
        setError(
          res.data.Error ||
          res.data.error ||
          res.data.message ||
          'Gebruikersnaam of wachtwoord is niet correct. Probeer opnieuw!'
        );
      }
    } catch (err) {
      const message = getApiErrorMessage(
        err,
        'Er is een fout opgetreden. Probeer opnieuw!'
      );

      setError(message);

    } finally {
      setIsSubmitting(false);
    }
  };

  const handle2FASubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) return;

    setError('');
    setIsSubmitting(true);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/2fa/login-check`,
        { challenge: twoFactorChallenge, token: totpCode },
        { withCredentials: true }
      );

      if (res.data.token) {
        login(res.data.token);
        navigate('/home', { replace: true });
      } else {
        setError('Ongeldige code. Probeer opnieuw.');
      }
    } catch (err) {
      const message = getApiErrorMessage(
        err,
        'Ongeldige code. Probeer opnieuw.'
      );

      setError(message);

    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light bg-gradient">
      <div
        className="shadow rounded-4 p-4 bg-white form-container-custom"
        style={{ width: '100%', maxWidth: '450px' }}
      >
        {!twoFactorRequired ? (
          <>
            <h2 className="text-center mb-4 text-primary">Welkom</h2>

            <form onSubmit={handleLogin}>
              <div className="mb-3">
                <label htmlFor="input_email" className="form-label">Emailadres</label>
                <input
                  id="input_email"
                  type="email"
                  className="form-control"
                  placeholder="jouw@email.nl"
                  value={values.email}
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, email: e.target.value }))
                  }
                  disabled={isSubmitting}
                  required
                />
              </div>

              <div className="mb-3">
                <label htmlFor="input_password" className="form-label">Wachtwoord</label>
                <div className="position-relative">
                  <input
                    id="input_password"
                    type={showPassword ? 'text' : 'password'}
                    className="form-control pe-5"
                    placeholder="Voer je wachtwoord in"
                    value={values.password}
                    onChange={(e) =>
                      setValues((prev) => ({ ...prev, password: e.target.value }))
                    }
                    disabled={isSubmitting}
                    required
                  />
                  <button
                    type="button"
                    className="btn btn-link position-absolute end-0 top-50 translate-middle-y p-0 me-2"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'}
                    disabled={isSubmitting}
                  >
                    <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                  </button>
                </div>
              </div>

              {error && (
                <div
                  className="alert alert-danger py-2 mb-3"
                  role="alert"
                  aria-live="assertive"
                >
                  {error}
                </div>
              )}

              <div className="d-grid mb-3">
                <button
                  id="loginButton"
                  type="submit"
                  className="btn btn-primary py-2"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Bezig met inloggen...' : 'Inloggen'}
                </button>
              </div>

              <div className="text-center">
                <Link to="/forgotpassword" className="link-secondary text-decoration-none">
                  Wachtwoord vergeten?
                </Link>
              </div>
            </form>
          </>
        ) : (
          <>
            <h2 className="text-center mb-2 text-primary">Twee-staps verificatie</h2>
            <p className="text-center text-muted mb-4" style={{ fontSize: '14px' }}>
              Voer de 6-cijferige code in uit je authenticator app.
            </p>

            <form onSubmit={handle2FASubmit}>
              <div className="mb-3">
                <label htmlFor="totpCode" className="form-label">Verificatiecode</label>
                <input
                  id="totpCode"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className="form-control text-center"
                  placeholder="000000"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  disabled={isSubmitting}
                  required
                  autoFocus
                />
              </div>

              {error && (
                <div
                  className="alert alert-danger py-2 mb-3"
                  role="alert"
                  aria-live="assertive"
                >
                  {error}
                </div>
              )}

              <div className="d-grid mb-3">
                <button
                  type="submit"
                  className="btn btn-primary py-2"
                  disabled={isSubmitting || totpCode.length !== 6}
                >
                  {isSubmitting ? 'Controleren...' : 'Bevestigen'}
                </button>
              </div>

              <div className="text-center">
                <button
                  type="button"
                  className="btn btn-link link-secondary text-decoration-none p-0"
                  disabled={isSubmitting}
                  onClick={() => {
                    setTwoFactorRequired(false);
                    setTwoFactorChallenge(null);
                    setTotpCode('');
                    setError('');
                  }}
                >
                  Terug naar inloggen
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default Login;
